// frontend/src/context/PermissionsContext.jsx
// DEBUG VERSION — remove console logs once working

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { permissionsAPI } from '../api/client';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

const OP_MAP = {
  view:    'has_permission',
  read:    'can_read',
  create:  'can_create',
  write:   'can_write',
  edit:    'can_write',
  delete:  'can_delete',
  export:  'can_export',
  execute: 'can_execute',
};

export const PermissionsProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading]   = useState(true);
  const [ready,   setReady]     = useState(false);  // true only after successful auth'd load
  const [error, setError]       = useState(null);

  const load = useCallback(async () => {
    console.group('[Permissions] load()');
    console.log('isAuthenticated:', isAuthenticated);
    console.log('user object:', JSON.stringify(user));
    console.log('user.user_type:', user?.user_type);

    if (!isAuthenticated) {
      console.log('-> not authenticated, clearing');
      console.groupEnd();
      setPermissions({});
      setReady(false);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      console.log('-> calling GET /permissions/my ...');
      const r = await permissionsAPI.getMyPermissions();
      console.log('-> HTTP status:', r.status);
      console.log('-> response:', JSON.stringify(r.data));

      if (r.data.success) {
        const map = {};
        for (const row of (r.data.permissions || [])) {
          map[row.menu_key] = row;
        }
        console.log('-> keys loaded:', Object.keys(map).join(', '));
        console.log('-> action_plans entry:', JSON.stringify(map['action_plans']));
        console.log('-> ap_settings entry:', JSON.stringify(map['ap_settings']));
        setPermissions(map);
        setReady(true);
      } else {
        console.warn('-> success=false:', r.data);
      }
      setError(null);
    } catch (err) {
      console.error('-> FAILED:', err.message, err.response?.data);
      setError(err.message);
    } finally {
      setLoading(false);
      console.groupEnd();
    }
  }, [isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  const can = useCallback((menuKey, operation = 'read') => {
    const uType = user?.userType || user?.user_type || '';
    if (uType === 'admin') return true;

    const row = permissions[menuKey];
    const col = OP_MAP[operation] ?? `can_${operation}`;
    const result = !!row?.[col];

    if (!result) {
      console.warn(
        `[Permissions] can('${menuKey}','${operation}') = FALSE`,
        '| col:', col,
        '| row:', row ?? 'MISSING',
        '| userType:', uType,
        '| loaded keys:', Object.keys(permissions).join(', ') || '(none)'
      );
    }
    return result;
  }, [permissions, user]);

  const permissionsFor = useCallback((menuKey) => {
    const uType = user?.userType || user?.user_type || '';
    if (uType === 'admin') {
      return { view:true, read:true, create:true, write:true, delete:true, export:true, execute:true };
    }
    const row = permissions[menuKey] || {};
    return {
      view:    !!row.has_permission,
      read:    !!row.can_read,
      create:  !!row.can_create,
      write:   !!row.can_write,
      delete:  !!row.can_delete,
      export:  !!row.can_export,
      execute: !!row.can_execute,
    };
  }, [permissions, user]);

  return (
    <PermissionsContext.Provider value={{ can, permissionsFor, loading, ready, error, reload: load }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be used inside <PermissionsProvider>');
  return ctx;
};

export default PermissionsContext;